'use client';

/**
 * Auth Session Provider
 *
 * Provides authentication context to the application using Better Auth's
 * React client. This enables useSession hook throughout the app.
 *
 * [Source: docs/stories/4-1-implement-better-auth-authentication.md#Task-3.6]
 */
import { createContext, useContext, type ReactNode } from 'react';
import { useSession as useBetterAuthSession } from '@pagelane/auth/client';

// Re-export the session hook for convenience
export { useSession as useBetterAuthSession } from '@pagelane/auth/client';

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { data: session, isPending } = useBetterAuthSession();

  const value: AuthContextValue = {
    isAuthenticated: !!session,
    isLoading: isPending,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
